import { APIClient } from "api/api_helper";
import { SESSION_API } from "api/url_helper";
import { CurrentUser, GetUserData } from "common/service/CommonService";
import {
  ScanSampleCollectionSummarySelectAllPayload,
  ScanSampleCollectionSummaryItem,
  ScanSampleSeperationSummarySelectAllPayload,
  ScanSampleSeperationSummaryItem,
  AccessioningSessionScanlogProgressHistoryPayload,
  AccessioningSessionScanlogProgressHistoryItem,
  ScanSampleCollectionTotalAttemptsHistoryPayload,
  ScanSampleCollectionTotalAttemptsHistoryItem,
  ScanSampleSeperationTotalAttemptsHistoryPayload,
  ScanSampleSeperationTotalAttemptsHistoryItem,
} from "../helper/session.types";

export type {
  ScanSampleCollectionSummarySelectAllPayload,
  ScanSampleCollectionSummaryItem,
  ScanSampleSeperationSummarySelectAllPayload,
  ScanSampleSeperationSummaryItem,
  AccessioningSessionScanlogProgressHistoryPayload,
  AccessioningSessionScanlogProgressHistoryItem,
  ScanSampleCollectionTotalAttemptsHistoryPayload,
  ScanSampleCollectionTotalAttemptsHistoryItem,
  ScanSampleSeperationTotalAttemptsHistoryPayload,
  ScanSampleSeperationTotalAttemptsHistoryItem,
};

const api = new APIClient();

export const sessionApiService = {
  insertData: (payload_data: any) => {
    const user = GetUserData() as CurrentUser;

    let stationNo = 0;
    if (typeof payload_data.dosingstationno === "number") {
      stationNo = payload_data.dosingstationno;
    } else if (payload_data.dosingstationno) {
      stationNo = parseInt(String(payload_data.dosingstationno).replace(/\D/g, ""), 10) || 0;
    } else if (payload_data.dosingStation) {
      stationNo = parseInt(String(payload_data.dosingStation).replace(/\D/g, ""), 10) || 0;
    }

    const payload = {
      studyid: payload_data.studycode,
      periodid: payload_data.period,
      timepoint_number: payload_data.timepoint,
      dosingstationno: stationNo,
      createdby: user.userid,
    };

    return api.create(SESSION_API.SESSION_API + "Insert", payload);
  },
  selectData: (payload: any) => {
    return api.create(SESSION_API.SESSION_API + "Select", payload);
  },
  selectAllData: (payload_data: any) => {
    const payload = {
      searchtext: payload_data?.searchtext || "",
      currentpage: payload_data?.currentpage || 1,
      pagesize: payload_data?.pagesize || null,
      studyid: payload_data?.studyid
    };
    return api.create(SESSION_API.SESSION_API + "SelectAll", payload);
  },
  updateData: (payload_data: any) => {
    const user = GetUserData() as CurrentUser;
    const payload = {
      sessionid: payload_data.sessionid,
      sessionstate: payload_data.sessionstate,
      updatedby: user?.userid,
      updatereason: "Update Session Status",
    };
    return api.create(SESSION_API.SESSION_API + "Update", payload);
  },
  scanProgressData: (payload_data: any) => {
    const user = GetUserData() as CurrentUser;
    const payload = {
      sessionid: payload_data?.sessionid,
      searchtext: payload_data?.searchtext || "",
      currentpage: payload_data?.currentpage ?? null,
      pagesize: payload_data?.pagesize ?? null,
      tabstatus: payload_data?.tabstatus || "ALL",
    };
    return api.create(SESSION_API.SESSION_API + "ScanProgress", payload);
  },
  scanlogProgressHistoryData: (payload_data: AccessioningSessionScanlogProgressHistoryPayload) => {
    const payload = {
      studyid: payload_data.studyid,
      searchtext: payload_data.searchtext ?? "",
      tabstatus: payload_data.tabstatus ?? "ALL",
      currentpage: payload_data.currentpage ?? 1,
      pagesize: payload_data.pagesize ?? 10,
      periodid: payload_data.periodid ?? null,
      timepointid: payload_data.timepointid ?? null,
      dosingstationno: payload_data.dosingstationno ?? null,
    };
    return api.create(SESSION_API.SESSION_API + "ScanlogProgressHistory", payload);
  },
  sessionDashboardData: (payload_data: any) => {
    const payload = {
      studyid: payload_data?.studyid ?? null,
      periodid: payload_data?.periodid ?? null,
    };
    return api.create(SESSION_API.SESSION_API + "SessionDashboard", payload);
  },
  globalReportSelectAllData: (payload_data: { studyid: number }) => {
    const user = GetUserData() as CurrentUser;
    const payload = {
      studyid: payload_data.studyid,
      generatedby: user.userid,
    };
    return api.create(SESSION_API.SESSION_API + "GlobalReport/SelectAll", payload, { responseType: "blob" });
  },
};


export const ScanSampleCollectionApiService = {
  insertData: (payload_data: any) => {
    const user = GetUserData() as CurrentUser;
    const payload = {
      sessionid: payload_data.sessionid,
      subjectbarcode: payload_data.subjectbarcode,
      vaccutainerbarcode: payload_data.vaccutainerbarcode,
      result: payload_data.result,
      resultmessage: payload_data.resultmessage,
      skipreason: payload_data.skipreason || "",
      scanby: payload_data.scanby ?? user?.userid,
    };
    return api.create(SESSION_API.SCAN_SAMPLE_COLLECTION_API + "Insert", payload);
  },
  validateSubjectBarcode: (payload_data: any) => {
    const user = GetUserData() as CurrentUser;
    const payload = {
      sessionid: payload_data.sessionid,
      scanby: payload_data.scanby ?? user?.userid,
      subjectbarcode: payload_data.subjectbarcode,
    };
    return api.create(
      SESSION_API.SCAN_SAMPLE_COLLECTION_API + "ValidateSubjectBarcode",
      payload
    );
  },
  selectAllData: (payload_data: any) => {
    console.log("payload_data : ", payload_data);
    const user = GetUserData() as CurrentUser;
    const payload = {
      currentpage: payload_data?.currentpage,
      pagesize: payload_data?.pagesize,
      searchtext: payload_data?.searchtext,
      sessionid: payload_data?.sessionid,
    };
    return api.create(SESSION_API.SCAN_SAMPLE_COLLECTION_API + "SelectAll", payload);
  },
  collectionSummarySelectAllData: (payload_data: ScanSampleCollectionSummarySelectAllPayload) => {
    const payload = {
      studyid: payload_data.studyid,
      periodid: payload_data.periodid ?? null,
      timepointid: payload_data.timepointid ?? null,
      dosingstationno: payload_data.dosingstationno ?? null,
      searchtext: payload_data.searchtext ?? "",
      currentpage: payload_data.currentpage ?? 1,
      pagesize: payload_data.pagesize ?? 10,
    };
    return api.create(SESSION_API.SCAN_SAMPLE_COLLECTION_API + "Collection/Summary/SelectAll", payload);
  },
  collectionSummaryReportSelectAllData: (payload_data: ScanSampleCollectionSummarySelectAllPayload) => {
    const user = GetUserData() as CurrentUser;
    const payload = {
      studyid: payload_data.studyid,
      periodid: payload_data.periodid ?? null,
      timepointid: payload_data.timepointid ?? null,
      dosingstationno: payload_data.dosingstationno ?? null,
      searchtext: payload_data.searchtext ?? "",
      userid: user?.userid,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
    return api.create(SESSION_API.SCAN_SAMPLE_COLLECTION_API + "Summary/Report/SelectAll", payload, { responseType: "blob" });
  },
  collectionSubjectReportSelectAllData: (payload_data: ScanSampleCollectionSummarySelectAllPayload) => {
    const user = GetUserData() as CurrentUser;
    const payload = {
      studyid: payload_data.studyid,
      periodid: payload_data.periodid ?? null,
      timepointid: payload_data.timepointid ?? null,
      dosingstationno: payload_data.dosingstationno ?? null,
      searchtext: payload_data.searchtext ?? "",
      subjectno: payload_data.subjectno ?? null,
      reporttype: payload_data.reporttype ? String(payload_data.reporttype).toUpperCase() : "SUMMARY",
      userid: user?.userid,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
    return api.create(SESSION_API.SCAN_SAMPLE_COLLECTION_API + "Summary/SubjectReport/SelectAll", payload, { responseType: "blob" });
  },
  totalAttemptsHistoryData: (payload_data: ScanSampleCollectionTotalAttemptsHistoryPayload) => {
    const payload = {
      sessionid: payload_data.sessionid,
      subjectbarcode: payload_data.subjectbarcode,
      currentpage: payload_data.currentpage ?? 1,
      pagesize: payload_data.pagesize ?? 5,
    };
    return api.create(SESSION_API.SCAN_SAMPLE_COLLECTION_API + "TotalAttemptsHistory", payload);
  }
};

export const ScanSampleSeperationApiService = {
  insertData: (payload_data: any) => {
    const user = GetUserData() as CurrentUser;
    const payload = {
      sessionid: payload_data.sessionid,
      aliquotbarcode: payload_data.aliquotbarcode,
      vaccutainerbarcode: payload_data.vaccutainerbarcode,
      result: payload_data.result,
      resultmessage: payload_data.resultmessage,
      skipreason: payload_data.skipreason || "",
      scanby: payload_data.scanby ?? user?.userid,
    };
    return api.create(SESSION_API.SCAN_SAMPLE_SEPERATION_API + "Insert", payload);
  },
  validateVaccutainerBarcode: (payload_data: any) => {
    const user = GetUserData() as CurrentUser;
    const payload = {
      sessionid: payload_data.sessionid,
      scanby: payload_data.scanby ?? user?.userid,
      vaccutainerbarcode: payload_data.vaccutainerbarcode,
    };
    return api.create(
      SESSION_API.SCAN_SAMPLE_SEPERATION_API + "ValidateVaccutainerBarcode",
      payload
    );
  },
  selectAllData: (payload_data: any) => {
    const user = GetUserData() as CurrentUser;
    const payload = {
      currentpage: payload_data?.currentpage,
      pagesize: payload_data?.pagesize,
      searchtext: payload_data?.searchtext,
      sessionid: payload_data?.sessionid,
    };
    return api.create(SESSION_API.SCAN_SAMPLE_SEPERATION_API + "SelectAll", payload);
  },
  seperationSummarySelectAllData: (payload_data: ScanSampleSeperationSummarySelectAllPayload) => {
    const payload = {
      studyid: payload_data.studyid,
      periodid: payload_data.periodid ?? null,
      timepointid: payload_data.timepointid ?? null,
      aliquotid: payload_data.aliquotid ?? null,
      dosingstationno: payload_data.dosingstationno ?? null,
      searchtext: payload_data.searchtext ?? "",
      currentpage: payload_data.currentpage ?? 1,
      pagesize: payload_data.pagesize ?? 10,
    };
    return api.create(SESSION_API.SCAN_SAMPLE_SEPERATION_API + "Seperation/Summary/SelectAll", payload);
  },
  seperationSummaryReportSelectAllData: (payload_data: ScanSampleSeperationSummarySelectAllPayload) => {
    const user = GetUserData() as CurrentUser;
    const payload = {
      studyid: payload_data.studyid,
      periodid: payload_data.periodid ?? null,
      timepointid: payload_data.timepointid ?? null,
      aliquotid: payload_data.aliquotid ?? null,
      dosingstationno: payload_data.dosingstationno ?? null,
      searchtext: payload_data.searchtext ?? "",
      userid: user?.userid,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
    return api.create(SESSION_API.SCAN_SAMPLE_SEPERATION_API + "Summary/Report/SelectAll", payload, { responseType: "blob" });
  },
  seperationSubjectReportSelectAllData: (payload_data: ScanSampleSeperationSummarySelectAllPayload) => {
    const user = GetUserData() as CurrentUser;
    const payload = {
      studyid: payload_data.studyid,
      periodid: payload_data.periodid ?? null,
      timepointid: payload_data.timepointid ?? null,
      aliquotid: payload_data.aliquotid ?? null,
      dosingstationno: payload_data.dosingstationno ?? null,
      searchtext: payload_data.searchtext ?? "",
      subjectno: payload_data.subjectno ?? null,
      userid: user?.userid,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
    return api.create(SESSION_API.SCAN_SAMPLE_SEPERATION_API + "Summary/SubjectReport/SelectAll", payload, { responseType: "blob" });
  },
  totalAttemptsHistoryData: (payload_data: ScanSampleSeperationTotalAttemptsHistoryPayload) => {
    const payload = {
      sessionid: payload_data.sessionid,
      vaccutainerbarcode: payload_data.vaccutainerbarcode,
      currentpage: payload_data.currentpage ?? 1,
      pagesize: payload_data.pagesize ?? 5,
    };
    return api.create(SESSION_API.SCAN_SAMPLE_SEPERATION_API + "TotalAttemptsHistory", payload);
  },
};

